<template>
	<div class="c-multiplayer-list">
		<SearchBar
			:multiplayer="true"
			:activeCategories="multiplayerCategories"
			v-model="params"
			@search="onSearch"
			@view="onView"
		></SearchBar>
		<div class="c-multiplayer-list__results" :class="{ 'has-paywall': showPaywall }">
			<div
				v-masonry="containerId"
				transition-duration="0.3s"
				item-selector=".c-grid-item"
			>
				<Item
					v-for="block in computedBlocks"
					:multiplayer="true"
					:ad="block"
					:key="block.block.id"
					:view="view"
					@onSubmit="onSubmit"
					v-masonry-tile
				></Item>
			</div>
			<div class="c-multiplayer-list__paywall" v-if="showPaywall">
				<article class="c-multiplayer-list__paywall__content">
					<h3>Unlock all Multiplayer ads</h3>
					<p>Full Multiplayer access is available on a paid plan.</p>
					<router-link class="btn btn-primary" to="/profile/billing">
						Subscribe
					</router-link>
				</article>
			</div>
		</div>
		<div
			class="c-multiplayer-list__empty-state"
			v-if="end && blocks.length > 0 && !isTrial"
		>
			<div class="c-multplayer-list__empty-state__inner">
				<h5>End of results</h5>
			</div>
		</div>
		<div
			class="c-multiplayer-list__empty-state"
			v-if="blocks.length === 0 && !processing"
		>
			<div class="c-multiplayer-list__empty-state__inner">
				<h5>No Ads found.</h5>
				<p>Try using other filters.</p>
			</div>
		</div>
	</div>
</template>

<script>
import SearchBar from '@swipekit/components/utils/search-bar';
import Item from '@swipekit/components/item';

import filter from '@swipekit/lib/filter';
import { getParams, setParams } from '@swipekit/lib/query-param-utils';

import api from '@swipekit/lib/api';

export default {
	components: {
		SearchBar,
		Item,
	},

	data: function () {
		return {
			containerId: null,
			params: {
				query: '',
				network: 'all',
				mediaType: 'all',
				activeAds: 0,
				categories: [],
				brands: [],
			},
			blocks: [],
			skip: 0,
			take: 0,

			end: false,

			view: '',
		};
	},

	props: {
		adIds: {
			type: Array,
			default: function () {
				return [];
			},
		},
	},

	watch: {
		$route: function () {},
		refreshFilters: function () {
			this.filter();
		},
		showSidebar: function () {
			this.$redrawVueMasonry();
		},
	},

	computed: {
		showSidebar: function () {
			return this.$store.getters['app/showSidebar'];
		},
		multiplayerCategories: function () {
			return this.$store.getters['app/multiplayerCategories'];
		},
		refreshFilters: function () {
			return this.$store.getters['app/refreshFilters'];
		},
		computedBlocks: function () {
			let blocks = this.blocks;

			if (!blocks) {
				return [];
			}

			let ids = this.adIds;

			for (let i = 0; i < blocks.length; i++) {
				let isSaved = false;

				if (ids.includes(blocks[i].id)) {
					isSaved = true;
				}

				blocks[i].isSaved = isSaved;
			}

			blocks = blocks.map((b) => {
				return {
					block: b,
				};
			});

			return blocks;
		},
		processing: function () {
			return this.$store.getters['app/processing'];
		},
		isTrial: function () {
			let workspace = this.$store.getters['workspaces/all'];

			if (!workspace) {
				return false;
			}

			return workspace.plan === 'TRIAL';
		},
		showPaywall: function () {
			return this.isTrial && this.blocks.length > 0 && !this.processing;
		},
	},

	methods: {
		async onView(view) {
			this.view = view;

			api.api.setOption('view', view);

			this.$nextTick(() => {
				this.$redrawVueMasonry();
			});
		},
		onSubmit: function (e) {
			this.$emit('onSubmit', e);
		},
		applyParams: function (params) {
			if (params.query) {
				this.params.query = params.query;
			}

			if (params.skip) {
				this.skip = params.skip;
			}
			if (params.take) {
				this.take = params.take;
			}

			if (params.categories) {
				this.params.categories = params.categories;
			}

			if (params.brands) {
				this.params.brands = params.brands;
			}

			if (params.mediaType) {
				this.params.mediaType = params.mediaType;
			}

			if (params.activeAds) {
				this.params.activeAds = params.activeAds;
			}

			if (params.network) {
				this.params.network = params.network;
			}
		},
		onEnd: function () {
			if (this.isTrial) {
				return;
			}
			if (this.processing) {
				return;
			}
			if (this.end) {
				return;
			}
			this.skip += this.take;
			this.filter();
		},
		async onSearch(params) {
			this.blocks = [];
			this.skip = 0;
			this.end = false;
			await this.filter(params);
		},
		async filter(params, firstTime = false) {
			if (this.processing) {
				return;
			}
			this.$store.dispatch('app/setProcessing', true);

			if (firstTime) {
				params = getParams();

				// if first time, force 1st page
				params.skip = 0;

				this.applyParams(params);
			}
			if (!params) {
				params = getParams();
			}
			if (this.isTrial) {
				this.skip = 0;
				this.take = 40;
			}

			params.skip = this.skip;
			params.take = this.take;

			setParams(params);

			const blocks = await filter(params);

			if (blocks.length < this.take) {
				this.end = true;
			}
			this.blocks = this.isTrial ? blocks : this.blocks.concat(blocks);

			this.$nextTick(() => {
				this.$redrawVueMasonry();
				this.$store.dispatch('app/setProcessing', false);
			});
		},
	},

	mounted: async function () {
		this.skip = 0;
		this.take = 40;
		this.filter(null, true);

		let options = await api.api.getOptions();

		this.view = options.view || 'all';
	},
};
</script>

<style lang="scss">
.c-multiplayer-list {
	&__results {
		position: relative;

		&.has-paywall {
			min-height: 600px;
			padding-bottom: 200px;
		}
	}

	&__paywall {
		position: absolute;
		z-index: 2;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 400px;
		box-sizing: border-box;
		display: flex;
		justify-content: center;
		align-items: center;
		padding: var(--spacer-sm);
		background: linear-gradient(
			to bottom,
			rgba(255, 255, 255, 0),
			rgba(255, 255, 255, 1) 45%
		);
		text-align: center;

		&__content {
			max-width: 420px;

			h3,
			p {
				margin-bottom: var(--margin-lg);
			}
		}
	}

	&__empty-state {
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		text-align: center;

		&__inner {
			display: inline-block;
			margin: 0 auto;
			border: var(--color-bg-4) solid 1px;
			border-radius: 10px;
			padding: var(--spacer-sm);

			h5 {
				margin-bottom: 0;
			}

			p {
				margin-bottom: 0;
			}
		}
	}
	&__empty-state {
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		text-align: center;

		&__inner {
			display: inline-block;
			margin: 0 auto;
			border: var(--color-bg-4) solid 1px;
			border-radius: 10px;
			padding: var(--spacer-sm);

			p {
				margin-bottom: 0;
			}
		}
	}
}
</style>
